import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import Nav from "@/components/Nav";
import Mascot from "@/components/Mascot";
import { makeApi } from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("checking");
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);

  useEffect(() => {
    if (!sid) return setState("error");
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") return !stop && setState("paid");
        if (["expired", "failed"].includes(r.data.payment_status)) return !stop && setState("error");
      } catch (e) {
        console.warn("Payment status check failed, retrying", e);
      }
      if (++tries < 15 && !stop) setTimeout(poll, 2000);
      else if (!stop) setState("timeout");
    };
    poll();
    return () => { stop = true; };
  }, [sid, api]);

  return (
    <>
      <Nav key={state} />
      <main className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="payment-success-page">
        <div className="flex justify-center"><Mascot size={160} /></div>
        {state === "checking" && <h1 className="mt-6 text-3xl font-black">Confirming your payment…</h1>}
        {state === "paid" && (<>
          <h1 className="mt-6 text-4xl font-black" data-testid="payment-confirmed">You're a Supporter!</h1>
          <p className="mt-2 text-slate-700">Ads are gone, lives are unlimited and you can now submit quizzes.</p>
        </>)}
        {state === "timeout" && <h1 className="mt-6 text-2xl font-black">Still processing — check back in a minute.</h1>}
        {state === "error" && <h1 className="mt-6 text-2xl font-black">Something went wrong with the payment.</h1>}
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/submit" className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black shadow-[0_4px_0_0_#374151]">Submit a quiz</Link>
          <Link to="/play" className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-white font-black shadow-[0_4px_0_0_#374151]">Play</Link>
        </div>
      </main>
    </>
  );
}

export function PaymentCancel() {
  return (
    <>
      <Nav />
      <main className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="payment-cancel-page">
        <h1 className="text-3xl font-black">No worries — checkout cancelled.</h1>
        <p className="mt-2 text-slate-700">You can become a Supporter any time.</p>
        <Link to="/pricing" className="mt-8 inline-block px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151]">Back to plans</Link>
      </main>
    </>
  );
}
